import React, { useState } from 'react';
import { Button, message, Modal, Spin, Tag, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectOrganization } from 'features/auth';
import Card from 'components/Card/Card';
import { Add, ArrowRight, CheckmarkFilled, ChevronDown, ChevronUp } from '@carbon/icons-react';
import { FACILITY_TYPE_ICONS } from '../providerConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { providerService } from 'services';
import { useAsync } from 'react-async-hook';
import { PROVIDER_APPLICATION_PROGRAM_TYPES } from '../SelectProgram/SelectProgram';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import FormattedAddress from 'components/FormattedAddress/FormattedAddress';
import { EMDASH } from 'constants';
import PageTitleBar from 'components/PageTitleBar/PageTitleBar';
import PageContainer from 'components/PageContainer/PageContainer';

const { Paragraph, Title } = Typography;

const getProgramName = (application) =>
  Object.values(PROVIDER_APPLICATION_PROGRAM_TYPES).find((pt) => pt.id === application?.programType?.id)?.name ||
  application?.programType?.name ||
  EMDASH;

export default function Applications() {
  const history = useHistory();
  const organization = useSelector(selectOrganization);

  const [careFacilities, setCareFacilities] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});

  const isExpanded = (facilityId) => expandedCards[facilityId] !== false;

  const toggleCard = (facilityId) => setExpandedCards((prev) => ({ ...prev, [facilityId]: !isExpanded(facilityId) }));

  const handleDeleteLocation = (careFacility) => {
    Modal.confirm({
      title: 'Delete Location?',
      content: (
        <div>
          <p>Are you sure you want to remove this location?</p>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Remove',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await providerService.deleteCareFacility(careFacility.id);
          message.success('Location removed');
          setCareFacilities((prev) => prev.filter((cf) => cf.id !== careFacility.id));
        } catch (error) {
          console.error('Error deleting care facility', error);
          message.error('Failed to remove location');
        }
      },
    });
  };

  useAsync(async () => {
    try {
      const _careFacilities = await providerService.getCareFacilities(organization.id);

      const _validities = await Promise.all(
        _careFacilities?.content.map((cf) => providerService.getCareFacilityProfileValidity(cf.id)),
      );

      const _applications = await Promise.all(
        _careFacilities?.content.map((cf, i) =>
          _validities?.[i]?.profileComplete === true ? providerService.getCarefacilityApplications(cf.id) : [],
        ),
      );

      _careFacilities?.content.forEach((cf, i) => {
        cf.applications = _applications[i];
        cf.profileValid = _validities?.[i]?.profileComplete;
      });

      setCareFacilities(_careFacilities?.content);
    } catch (error) {
      console.log('Error fetching care facilities', error);
      message.error('Failed to fetch care facilities');
    }
  }, [organization.id]);

  return (
    <PageContainer>
      <PageMeta title="My Applications" />

      <div id="page-top-placeholder" />

      <PageTitleBar
        title="Locations and programs"
        extra={
          <Button
            ghost
            icon={<Add size={22} className="ml-[-6px] mr-[-2px] mt-[2px]" />}
            onClick={() => history.push('/provider/facility')}
            data-testid="add-location-cta"
          >
            Add location
          </Button>
        }
      />

      {!Array.isArray(careFacilities) ? (
        <div className="p-16">
          <Spin className="table m-auto" tip="Loading...">
            <div></div>
          </Spin>
        </div>
      ) : (
        <div className="px-4 md:px-0 flex flex-col gap-4 mt-8 mb-8">
          {careFacilities.map((careFacility) => {
            const hasApps =
              careFacility.profileValid &&
              Array.isArray(careFacility.applications) &&
              careFacility.applications.length > 0;
            const collapsible = hasApps;
            const expanded = isExpanded(careFacility.id);

            const applicationCount = careFacility.applications?.length || 0;

            const FacilityTypeIcon = FACILITY_TYPE_ICONS[careFacility.type?.title];

            return (
              <Card
                key={careFacility.id}
                data-testid={`care-facility-${careFacility.id}`}
                noBodyPadding
                className="border border-solid !border-gray-400 !border-opacity-50 overflow-hidden"
              >
                {/* ── Header (darker bg) ── */}
                <div className="relative p-6 bg-black/[0.03]">
                  {/* Top-right action */}
                  {!careFacility.profileValid ? (
                    <Button
                      type="text"
                      danger
                      size="small"
                      className="!absolute top-4 right-4"
                      onClick={() => handleDeleteLocation(careFacility)}
                      data-testid="remove-location-btn"
                    >
                      Remove
                    </Button>
                  ) : collapsible ? (
                    <Button
                      type="text"
                      size="small"
                      className="!absolute top-4 right-4 text-gray-500"
                      icon={expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      onClick={() => toggleCard(careFacility.id)}
                    />
                  ) : null}

                  {/* Facility info */}
                  <Title level={4} className="!mb-0.5 font-bold pr-10">
                    {careFacility.displayName || EMDASH}
                  </Title>

                  {careFacility.address && (
                    <Paragraph className="!mb-0.5 text-gray-600">
                      <FormattedAddress address={careFacility.address} singleLine />
                    </Paragraph>
                  )}

                  {(careFacility.type?.title || careFacility.id) && (
                    <Tag className="mt-1 mb-1 inline-flex items-center gap-1.5">
                      {FacilityTypeIcon && <FacilityTypeIcon size={12} />}
                      {careFacility.type?.title}
                    </Tag>
                  )}

                  {/* ── Incomplete location ── */}
                  {!careFacility.profileValid && (
                    <div className="mt-5">
                      <Link to={`/provider/facility/${careFacility.id}`} data-testid="finish-adding-location">
                        <Button type="primary" icon={<ArrowRight size={16} />} iconPosition="end">
                          Finish adding location
                        </Button>
                      </Link>
                    </div>
                  )}

                  {/* ── Complete, no applications ── */}
                  {careFacility.profileValid && !hasApps && (
                    <div className="flex items-center gap-2 mt-5">
                      <Link to={`/provider/facility/${careFacility.id}`}>
                        <Button type="link" className="!px-0 !text-gray-700">
                          View location
                        </Button>
                      </Link>
                      <Link to={`/provider/${careFacility.id}/add-application`} data-testid="apply-for-fee-assistance">
                        <Button type="primary" icon={<ArrowRight size={16} />} iconPosition="end">
                          Apply for fee assistance
                        </Button>
                      </Link>
                    </div>
                  )}

                  {/* ── Has apps expanded: "View location" as block ── */}
                  {hasApps && expanded && (
                    <Link to={`/provider/facility/${careFacility.id}`} className="block mt-5">
                      <Button type="link" className="!px-0 !text-gray-700">
                        View location
                      </Button>
                    </Link>
                  )}

                  {/* ── Collapsed: all three inline ── */}
                  {hasApps && !expanded && (
                    <div className="flex items-center gap-2 mt-5 text-sm text-gray-500 flex-wrap">
                      <Link
                        to={`/provider/facility/${careFacility.id}`}
                        className="!text-gray-500 !hover:text-gray-700"
                      >
                        View location
                      </Link>

                      {applicationCount > 0 && (
                        <>
                          <span>·</span>
                          <span>
                            {applicationCount} {applicationCount === 1 ? 'application' : 'applications'}
                          </span>
                        </>
                      )}

                      <span>·</span>
                      <Link
                        to={`/provider/${careFacility.id}/add-application`}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        + Apply for another
                      </Link>
                    </div>
                  )}
                </div>

                {/* ── Programs body (white bg, only when expanded) ── */}
                {hasApps && expanded && (
                  <div className="p-6">
                    <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-3">Programs</p>
                    <div className="border border-solid border-gray-200 rounded divide-y divide-solid divide-gray-200">
                      {careFacility.applications.map((application) => {
                        const description = application?.status?.status?.description;
                        const normalized = description?.toLowerCase();
                        const isIncomplete = normalized === 'incomplete';
                        const isNew = normalized === 'new';

                        return (
                          <div
                            key={application.id}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 gap-1 sm:gap-3"
                          >
                            <span className="font-semibold">{getProgramName(application)}</span>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                              <div className="flex items-center gap-2">
                                {isIncomplete && <span className="w-3 h-3 rounded-full bg-orange-400 shrink-0" />}
                                {isNew && <CheckmarkFilled size={16} className="text-green-600 shrink-0" />}
                                <span
                                  className={
                                    isIncomplete
                                      ? 'text-orange-500 font-medium'
                                      : isNew
                                      ? 'text-green-600'
                                      : 'text-gray-700'
                                  }
                                >
                                  {description || EMDASH}
                                </span>
                              </div>
                              <Link to={`/provider/applications/${application.id}`} data-testid="application-cta">
                                <Button type="link" className="!px-0 whitespace-nowrap">
                                  {isIncomplete ? 'Resume application →' : 'View application'}
                                </Button>
                              </Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <Link to={`/provider/${careFacility.id}/add-application`}>
                      <Button type="link" className="!px-0 mt-4">
                        + Apply for another
                      </Button>
                    </Link>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}
